import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Icon, Input } from '../../components';
import { PathsConfig } from '../../services/httpService/configPaths';
import { httpService } from '../../services/httpService/httpService';
import { setAuthToken } from '../../services/authToken/authTokenService';
import { setEmbedded } from '../../services/embedded/embeddedService';
import { setOpenSuccessErrorModal } from '../../storeSlices/errorSuccessSlice';
import { setIsAuthenticated } from '../../storeSlices/userSlice';
import { loginValidation } from './Login.utils';
import { images } from '../../constants/images';
import styles from './Login.module.scss';
import loaderStyles from '../../components/Loader/Loader.module.scss';

const initialDetails = {
	email: '',
	emailError: false,
	password: '',
	passwordError: false,
};

const isSandbox = window._env_?.NODE_ENV === 'sandbox';

export const Login = () => {
	const { t } = useTranslation('translation');

	const [searchParams, setSearchParams] = useSearchParams();

	const [loginDetails, setLoginDetails] = useState(initialDetails);
	const [loginErrorMessage, setLoginErrorMessage] = useState<string>();
	const [showPassword, setShowPassword] = useState(false);
	const [isTokenLoginInProgress, setIsTokenLoginInProgress] = useState(() => Boolean(searchParams.get('token')));

	const dispatch = useDispatch();
	const navigate = useNavigate();

	// invite user flow will reach here and validate email on the server before login
	useEffect(() => {
		const userName = searchParams.get('verificationKey');
		userName && verifyEmailFromInvitation(userName);

		const token = searchParams.get('token');
		if (token) {
			loginWithToken(token);
		}
	}, []);

	const validateData = async () => {
		setLoginErrorMessage(undefined);
		const { isValid, data } = loginValidation(loginDetails);
		if (isValid) {
			await login();
		} else {
			setLoginErrorMessage(t('incorrectCredentials'));
			setLoginDetails(data);
		}
	};

	const login = async () => {
		try {
			const res = await httpService({
				dispatch,
				path: PathsConfig.authLogin,
				data: {
					email: loginDetails.email,
					password: loginDetails.password,
				},
			});
			dispatch(setIsAuthenticated(true));
			setLoginErrorMessage(undefined);
			navigate('/');
		} catch (e: any) {
			if (e.response?.data?.message) {
				setLoginErrorMessage(e.response?.data?.message);
			} else {
				dispatch(setOpenSuccessErrorModal({ responseError: e }));
			}
		}
	};

	const loginWithToken = async (token: string) => {
		try {
			const res = await httpService({
				dispatch,
				path: PathsConfig.authLoginWithToken,
				data: { token },
			});
			// Persist the JWT for the embedded (iframe) flow, where the session cookie is
			// dropped cross-origin. The magic link is single-use, so this must survive reloads.
			res?.data?.access_token && setAuthToken(res.data.access_token);
			dispatch(setIsAuthenticated(true));
			setLoginErrorMessage(undefined);
			const redirectTo = res?.data?.redirectTo || '/';
			// The magic link is the embedded (iframe) entry point. `navigate` is an SPA
			// transition, so the app never re-reads the URL — and by the time the
			// `?embeddedPage=true` destination renders, isEmbedded() may have already cached
			// `false` from the token-login URL. Resolve embedded mode from redirectTo here,
			// before navigating, so the flag is set regardless of render timing or whether
			// third-party sessionStorage is available.
			const queryStart = redirectTo.indexOf('?');
			if (queryStart !== -1 && new URLSearchParams(redirectTo.slice(queryStart + 1)).get('embeddedPage') === 'true') {
				setEmbedded(true);
			}
			navigate(redirectTo);
		} catch (e: any) {
			setIsTokenLoginInProgress(false);
			if (e.response?.data?.message) {
				setLoginErrorMessage(e.response?.data?.message);
			} else {
				setLoginErrorMessage(t('incorrectCredentials'));
			}
		}
	};

	const verifyEmailFromInvitation = async (userName: string) => {
		try {
			await httpService({
				dispatch,
				path: PathsConfig.authVerifyUser,
				urlParam: { userName },
			});
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error }));
		}
	};

	const updateForm = (field: string, data: string) => {
		setLoginDetails((prev) => ({ ...prev, [field]: data, [`${field}Error`]: false }));
	};

	return isTokenLoginInProgress ? (
		// Token (magic-link) login, e.g. the embedded iframe flow: show a clean full-page
		// loader instead of flashing the branded login form before redirecting.
		<div className={loaderStyles.container}>
			<div className={loaderStyles.loaderIcon} />
		</div>
	) : (
		<div className={styles.globalPageContainer}>
			<div className={styles.container} onKeyDown={(event) => event.key === 'Enter' && validateData()}>
				<div className={styles.leftContainer}>
					<div className={styles.header}>
						<Icon imgType='logo' height={2.5} width={12.3} color='black' />
					</div>
					<div className={styles.content}>
						<div className={styles.loginContainer}>
							<div className={styles.loginTitleContainer}>
								<h1 className={styles.title}>{t('auth.welcomeBack')}</h1>
								<span className={styles.description}>{t('auth.welcomeBackDetails')}</span>
							</div>
							<div className={styles.loginFields}>
										<Input
											value={loginDetails.email}
											onChange={(value) => updateForm('email', value)}
											title={t('email')}
											showError={loginDetails.emailError}
											type='email'
											placeholder={t('enterYourEmail')}
										/>
										<Input
											value={loginDetails.password}
											onChange={(value) => updateForm('password', value)}
											title={t('password')}
											showError={loginDetails.passwordError}
											type={showPassword ? undefined : 'password'}
											placeholder='••••••••••'
											rightChildren={
												<Button type='link' color='neutral' onClick={() => setShowPassword(!showPassword)} className={styles.eyeButton}>
													<Icon imgType={showPassword ? 'closed_eye' : 'eye'} />
												</Button>
											}
										/>
										<Button className={styles.signInButton} onClick={validateData}>
											{t('auth.signIn')}
										</Button>
										{isSandbox && (
											<div style={{ textAlign: 'center' }}>
												<span className={styles.description}>No sandbox account? </span>
												<Button type='link' onClick={() => navigate('/signup')}>
													Create Sandbox Account
												</Button>
											</div>
										)}
									</div>
									<div className={styles.errorBox} style={{ visibility: loginErrorMessage ? 'visible' : 'hidden' }}>
										<Icon imgType='warning_sign' width={2} color='destructive' />
										<span>{loginErrorMessage}</span>

										<Button type='link' color='neutral' onClick={() => setLoginErrorMessage(undefined)}>
											<Icon imgType='x_icon' color='destructive' className={styles.x_icon} />
										</Button>
								</div>
							</div>
						</div>
					</div>
				<div className={styles.rightContainer}>
					<img alt='login' src={images.login} className={styles.image} />
				</div>
			</div>
		</div>
	);
};
